/**
 * useSupabaseUpload — Sprint B Supabase upload hook.
 *
 * Pipeline:
 *   1. Validate active session + non-null projectId.
 *   2. Generate scene_id via crypto.randomUUID().
 *   3. Upload raw file to storage bucket "uploads" at:
 *        `${user_id}/${scene_id}/${file.name}`
 *      with { upsert: false }.
 *   4. Insert a row into `scene_files` (file_type inferred from extension).
 *   5. Insert a row into `scenes` (id = scene_id, source_type = 'upload').
 *   6. Invalidate ['supabase', 'scenes', projectId] and ['supabase', 'projects'].
 *
 * On ANY post-upload failure (insert errors), best-effort delete the uploaded
 * blob via storage.remove([path]). The cleanup error is swallowed (logged only)
 * so the caller sees the ORIGINAL error message — that is what they need to
 * react to. If cleanup fails, we have a tolerable orphan; if we re-throw the
 * cleanup error instead, the caller loses the actionable diagnostic.
 *
 * Mutation signature:  mutate({ file: File }) → { sceneId: string, storagePath: string }
 *
 * The narrow camelCase return shape lets callers do:
 *   const { sceneId } = await mutateAsync({ file });
 *   setActiveScene(sceneId);
 * UploadResponse's snake_case is the legacy axios contract — not used here.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '../lib/supabase/client';
import type {
  SceneFileType,
  SupabaseSceneFileInsert,
  SupabaseSceneInsert,
} from '../types/supabase';

export interface UseSupabaseUploadVariables {
  file: File;
}

export interface UseSupabaseUploadResult {
  sceneId: string;
  storagePath: string;
}

/**
 * Map a filename extension to the Postgres `scene_file_type` enum.
 * `.laz` is stored as `'las'` because the enum has no separate `laz` member —
 * downstream consumers should treat las/laz as the same handler.
 */
function inferFileType(fileName: string): SceneFileType {
  const lower = fileName.toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot === -1 || dot === lower.length - 1) {
    throw new Error(`unsupported file type: ${fileName}`);
  }
  const ext = lower.slice(dot + 1);
  switch (ext) {
    case 'las':
    case 'laz':
      return 'las';
    case 'tif':
    case 'tiff':
      return 'dem';
    case 'csv':
      return 'csv';
    case 'xyz':
      return 'xyz';
    default:
      throw new Error(`unsupported file type: ${fileName}`);
  }
}

export function useSupabaseUpload(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<
    UseSupabaseUploadResult,
    Error,
    UseSupabaseUploadVariables
  >({
    mutationFn: async ({ file }) => {
      if (!projectId) {
        throw new Error('useSupabaseUpload: projectId is required');
      }

      const supabase = getSupabase();

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) {
        throw new Error(
          `useSupabaseUpload: session lookup failed: ${sessionError.message}`,
        );
      }
      const session = sessionData?.session ?? null;
      if (!session?.user?.id) {
        throw new Error(
          'useSupabaseUpload: no active session — sign in before uploading',
        );
      }
      const userId = session.user.id;

      // Throws BEFORE upload if extension is unsupported, so no orphan blob.
      const fileType = inferFileType(file.name);

      const sceneId = crypto.randomUUID();
      const storagePath = `${userId}/${sceneId}/${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(storagePath, file, { upsert: false });
      if (uploadError) {
        throw new Error(
          `useSupabaseUpload: storage upload failed: ${uploadError.message}`,
        );
      }

      // From here on, any failure must trigger best-effort blob cleanup.
      const cleanup = async (originalMessage: string): Promise<never> => {
        try {
          const { error: removeError } = await supabase.storage
            .from('uploads')
            .remove([storagePath]);
          if (removeError) {
            // Log but don't surface — the caller cares about originalMessage.
            // eslint-disable-next-line no-console
            console.warn(
              `useSupabaseUpload: cleanup failed for ${storagePath}: ${removeError.message}`,
            );
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(
            `useSupabaseUpload: cleanup threw for ${storagePath}: ${String(e)}`,
          );
        }
        throw new Error(originalMessage);
      };

      const sceneFileRow: SupabaseSceneFileInsert = {
        scene_id: sceneId,
        file_type: fileType,
        original_name: file.name,
        storage_path: storagePath,
        file_size_bytes: file.size,
      };
      const { error: sfError } = await supabase
        .from('scene_files')
        .insert(sceneFileRow);
      if (sfError) {
        return cleanup(
          `useSupabaseUpload: scene_files insert failed: ${sfError.message}`,
        );
      }

      const sceneRow: SupabaseSceneInsert & { id: string } = {
        id: sceneId,
        project_id: projectId,
        name: file.name,
        source_type: 'upload',
        storage_path: storagePath,
      };
      const { error: sceneError } = await supabase
        .from('scenes')
        .insert(sceneRow);
      if (sceneError) {
        return cleanup(
          `useSupabaseUpload: scenes insert failed: ${sceneError.message}`,
        );
      }

      return { sceneId, storagePath };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['supabase', 'scenes', projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['supabase', 'projects'],
      });
    },
  });
}
